import {
  BackButton,
  Container,
  Page,
  PageTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  Team,
  TeamRole,
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  deleteInvitation,
  readInvitations,
  readTeamMembers,
} from '../../common/api/team';
import { RequireUserTeam } from '../../common/auth/RequireTeam';
import {
  TeamJoinType,
  TeamMemberPendingRequestStatus,
} from '../../common/enums';
import { removeInvitee, setInvitees } from '../../common/reducers/inviteeSlice';
import { setTeamMembers } from '../../common/reducers/teamMemberSlice';
import { InviteeTable } from './InviteeTable';
import { InviteFrom, inviteFromResolver, InviteUser } from './InviteUser';
import { TeamMemberTable } from './TeamMemberTable';
import { UserRequestList } from './UserRequestList';

enum SeletedTeamView {
  RequestToJoin,
  PendingInvites,
  CurrentTeam,
}

export const TeamManagement: React.FunctionComponent =
  (): JSX.Element | null => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { teams, currentTeam, setCurrentTeam } = useTeam();
    const { currentOrg } = useOrganization();
    const invitees: Invitee[] = useSelector<AppState, Invitee[]>(
      ({ invitees }) => {
        return invitees;
      }
    );
    const teamMembers: TeamMember[] = useSelector<AppState, TeamMember[]>(
      ({ teamMembers }) => {
        return teamMembers;
      }
    );
    const filterPendingRequests = () => {
      return invitees
        .filter(
          (x) =>
            x.requestType !== TeamJoinType.invitation &&
            x.status === TeamMemberPendingRequestStatus.SENT
        )
        .map((x) => {
          return {
            id: x.id,
            email: x.email,
            role: TeamRole[x.role],
            status: TeamMemberPendingRequestStatus[x.status],
            fullName: x.fullName,
          };
        });
    };
    const [pendingRequests, setPendingRequests] = React.useState<Invitee[]>(
      filterPendingRequests()
    );
    const [selectedTeamView, setSelectedTeamView] =
      React.useState<SeletedTeamView>(SeletedTeamView.CurrentTeam);
    const {
      register,
      handleSubmit,
      reset,
      formState: { errors, isValid, isSubmitting },
    } = useForm<InviteFrom>({
      mode: 'onChange',
      defaultValues: {
        email: '',
      },
      resolver: inviteFromResolver,
    });

    const removeFromTable = (id: string) => {
      dispatch(removeInvitee(id));
    };

    useEffect(() => {
      if (currentTeam && currentTeam.id !== id) {
        const selectedTeam = teams?.find((team) => team.id === id);
        if (selectedTeam) {
          setCurrentTeam(selectedTeam);
        } else {
          navigate(`/organization`);
        }
      }
    }, [id]);

    useEffect(() => {
      if (currentTeam && currentTeam.id === id) {
        const getInvitees = async () => {
          try {
            const invitations = await readInvitations(
              currentOrg?.id as string,
              currentTeam?.id as string
            );
            const newInvitees = invitations.map((x) => {
              return {
                id: x.id,
                email: x.emailId,
                role: TeamRole[x.role],
                requestType: x.requestType,
                status: TeamMemberPendingRequestStatus[x.status],
                fullName: x.userFullName,
              };
            });
            if (newInvitees) {
              dispatch(setInvitees(newInvitees));
            }
          } catch (_) {
            //TODO: Handle error state or logging
          }
        };
        getInvitees();
      }
    }, [isSubmitting === true, currentTeam]);

    useEffect(() => {
      const teamRequests = filterPendingRequests();
      if (teamRequests) {
        setPendingRequests(teamRequests);
      }
    }, [invitees]);

    useEffect(() => {
      if (currentTeam && currentTeam.id !== id && teams && teams.length > 0) {
        setCurrentTeam(teams.find((t) => t.id === id) as Team);
      }
    }, []);

    useEffect(() => {
      if (currentTeam && currentTeam.id !== id && teams && teams.length > 0) {
        setCurrentTeam(teams.find((t) => t.id === id) as Team);
      }
    }, []);

    useEffect(() => {
      if (
        currentTeam &&
        currentTeam.id === id &&
        currentTeam?.role !== TeamRole.DEVELOPER_TEAM_ADMIN
      ) {
        navigate(`/team`);
      } else if (currentOrg && currentTeam && currentTeam.id === id) {
        const getTeamMembers = async () => {
          const members = await readTeamMembers(
            currentOrg?.id as string,
            currentTeam?.id as string
          );
          if (members) {
            dispatch(setTeamMembers(members));
          }
        };
        getTeamMembers();
      }
    }, [currentOrg, currentTeam]);

    const deleteInvite = (invitee: Invitee) => {
      deleteInvitation(
        currentOrg?.id as string,
        currentTeam?.id as string,
        invitee.email,
        invitee.id
      );
      dispatch(removeInvitee(invitee.id));
    };

    const removeInvitation = async (id: string) => {
      setPendingRequests(
        [...pendingRequests].filter((member) => member.id !== id)
      );
    };

    if (!currentOrg || !currentTeam || currentTeam.id !== id) {
      return null;
    }

    return (
      <RequireUserTeam>
        <Page>
          <Container size="medium">
            <div className="flex flex-row items-center gap-x-3 pb-os1-spacing">
              <BackButton />
              <PageTitle>{`${currentTeam.name}`}</PageTitle>
            </div>
            <div className="flex flex-row mb-4">
              <div
                className={`${
                  selectedTeamView === SeletedTeamView.RequestToJoin
                    ? 'border-brand-blue'
                    : 'border-gray-200'
                } flex flex-row gap-x-2 border-b-2 pr-os1-spacing pb-4`}
              >
                <input
                  className={`${
                    selectedTeamView === SeletedTeamView.RequestToJoin
                      ? 'text-brand-blue'
                      : 'text-black'
                  } font-medium bg-transparent text-sm outline-none
          focus:outline-none active:text-purple-500 ease-linear transition-all duration-150 cursor-pointer `}
                  onClick={() =>
                    setSelectedTeamView(SeletedTeamView.RequestToJoin)
                  }
                  type="button"
                  value="Requested to Join"
                />
                <div
                  className={`${
                    selectedTeamView === SeletedTeamView.RequestToJoin
                      ? 'bg-indigo-100 text-brand-blue'
                      : 'bg-gray-200 text-black'
                  } font-medium flex flex-col justify-center items-center w-8 rounded-full h-5 mt-1`}
                >
                  <span className="text-sm">{pendingRequests.length || 0}</span>
                </div>
              </div>
              <div
                className={`${
                  selectedTeamView === SeletedTeamView.PendingInvites
                    ? 'border-brand-blue'
                    : 'border-gray-200'
                } flex flex-row gap-x-2 border-b-2 pr-os1-spacing pb-4`}
              >
                <input
                  className={`${
                    selectedTeamView === SeletedTeamView.PendingInvites
                      ? 'text-brand-blue'
                      : 'text-black'
                  } font-medium bg-transparent text-sm outline-none
          focus:outline-none active:text-purple-500 ease-linear transition-all duration-150 cursor-pointer `}
                  onClick={() =>
                    setSelectedTeamView(SeletedTeamView.PendingInvites)
                  }
                  type="button"
                  value="Pending Invites"
                />
                <div
                  className={`${
                    selectedTeamView === SeletedTeamView.PendingInvites
                      ? 'bg-indigo-100 text-brand-blue'
                      : 'bg-gray-200 text-black'
                  } font-medium flex flex-col justify-center items-center w-8 rounded-full h-5 mt-1`}
                >
                  <span className="text-sm">{invitees.length || 0}</span>
                </div>
              </div>
              <div
                className={`${
                  selectedTeamView === SeletedTeamView.CurrentTeam
                    ? 'border-brand-blue'
                    : 'border-gray-200'
                } flex flex-row gap-x-2 border-b-2 pr-os1-spacing pb-4`}
              >
                <input
                  className={`${
                    selectedTeamView === SeletedTeamView.CurrentTeam
                      ? 'text-brand-blue'
                      : 'text-black'
                  } font-medium bg-transparent text-sm outline-none
          focus:outline-none active:text-purple-500 ease-linear transition-all duration-150 cursor-pointer `}
                  onClick={() =>
                    setSelectedTeamView(SeletedTeamView.CurrentTeam)
                  }
                  type="button"
                  value="Current Team"
                />
                <div
                  className={`${
                    selectedTeamView === SeletedTeamView.CurrentTeam
                      ? 'bg-indigo-100 text-brand-blue'
                      : 'bg-gray-200 text-black'
                  } font-medium flex flex-col justify-center items-center w-8 rounded-full h-5 mt-1`}
                >
                  <span className="text-sm">{teamMembers.length || 0}</span>
                </div>
              </div>
              <div className="flex flex row border-b-2 border-gray-200 pr-os1-spacing pb-4 w-full" />
            </div>
            <div>
              {selectedTeamView === SeletedTeamView.CurrentTeam && (
                <div>
                  <InviteUser
                    errors={errors}
                    handleSubmit={handleSubmit}
                    isValid={isValid}
                    register={register}
                    reset={reset}
                  />
                  <TeamMemberTable />
                </div>
              )}
              {selectedTeamView === SeletedTeamView.PendingInvites && (
                <InviteeTable
                  deleteInvite={deleteInvite}
                  inviteeList={invitees}
                  removeFromTable={removeFromTable}
                />
              )}
              {selectedTeamView === SeletedTeamView.RequestToJoin && (
                <UserRequestList
                  pendingRequests={pendingRequests}
                  removeInvitation={removeInvitation}
                />
              )}
            </div>
          </Container>
        </Page>
      </RequireUserTeam>
    );
  };
